import React, { useEffect, useMemo, useState } from 'react';
import { DashCircle, PlusCircle } from 'react-bootstrap-icons';
import MixedVariableCell from './MixedVariableCell';
import VariableCell from './VariableCell';
import SheetTextInput from './inputs/SheetTextInput';
import SheetCheckbox from './inputs/SheetCheckbox';
import { buildRenderableCells, SHEET_DISPLAY_SCALE } from './sheetLayoutHelpers';
import {
  getValueAtPath,
  parseTokenPath
} from './documentStateHelpers';
import {
  getCustomCellBinding,
  getCustomTableCellValue,
  getExpandableVisibleRowCount,
  getTableFieldDatalistOptions,
  normalizeKeyValueTable,
  resolveCustomSheetLayoutsWithRowShift
} from './customSheetFields';
import { expandSheetForCustomTables } from './expandSheetForCustomTables';
import { useSheetFormulaEngine } from './formulaEngine';
import { isSheetErrorDisplay } from './formulaEngine/coerceSheetValue';
import { resolveVariableFieldBinding } from './resolveVariableField';
import { getPrimaryVariableToken, isMixedVariableCell } from './variableTokenHelpers';
import {
  buildCheckboxIndexMap,
  getSheetCheckboxValue,
  listSheetCheckboxes
} from './sheetCheckboxHelpers';
import './SheetRenderer.scss';

const ACTION_ROW_TRACK = '32px';

const parseGridSpan = span => {
  const match = String(span || '').match(/^(\d+)\s*\/\s*(\d+)$/);
  if (!match) return null;
  return { start: Number(match[1]), end: Number(match[2]) };
};

/** Insert compact action tracks directly under each expandable table's last data row. */
const injectExpandableActionBars = (layout, expandableActions) => {
  if (!layout || !expandableActions?.length) {
    return { ...layout, actionBars: [] };
  }

  const rowLineBySheetRow = {};
  (layout.visibleRows || []).forEach((row0, index) => {
    rowLineBySheetRow[row0 + 1] = index + 1;
  });

  const sorted = [...expandableActions].sort((a, b) => a.lastRow1 - b.lastRow1);
  let tracks = [...(layout.gridTemplateRowTracks || [])];
  let cells = (layout.cells || []).map(cell => {
    const span = parseGridSpan(cell.gridRow);
    return span ? { ...cell, gridRowStart: span.start, gridRowEnd: span.end } : { ...cell };
  });
  const actionBars = [];
  let shift = 0;

  sorted.forEach(action => {
    const baseLine = rowLineBySheetRow[action.lastRow1];
    if (!baseLine || baseLine < 1) return;

    const line = baseLine + shift;
    tracks.splice(line, 0, ACTION_ROW_TRACK);

    cells = cells.map(cell => {
      if (cell.gridRowStart == null) return cell;
      if (cell.gridRowStart > line) {
        return {
          ...cell,
          gridRowStart: cell.gridRowStart + 1,
          gridRowEnd: cell.gridRowEnd + 1
        };
      }
      if (cell.gridRowEnd > line + 1) {
        return { ...cell, gridRowEnd: cell.gridRowEnd + 1 };
      }
      return cell;
    });

    actionBars.push({
      ...action,
      gridRow: `${line + 1} / ${line + 2}`,
      gridColumn: '1 / -1'
    });
    shift += 1;
  });

  return {
    ...layout,
    cells: cells.map(cell =>
      cell.gridRowStart != null
        ? {
            ...cell,
            gridRow: `${cell.gridRowStart} / ${cell.gridRowEnd}`
          }
        : cell
    ),
    gridTemplateRowTracks: tracks,
    gridTemplateRows: tracks.join(' '),
    actionBars
  };
};

const ExpandableTableActions = ({
  action,
  onExpandableTableAddRow,
  onExpandableTableRemoveRow
}) => {
  const { layout, visible, storedCount, canAdd, canRemove } = action;

  return (
    <div
      className="dochub-sheet-renderer__table-actions"
      style={{ gridRow: action.gridRow, gridColumn: action.gridColumn }}
    >
      <button
        type="button"
        className="dochub-sheet-renderer__table-action-btn is-add"
        disabled={!canAdd || !onExpandableTableAddRow}
        onClick={() => onExpandableTableAddRow?.(layout.field, layout)}
      >
        <PlusCircle />
        <span>Add row</span>
      </button>
      {canRemove && (
        <button
          type="button"
          className="dochub-sheet-renderer__table-action-btn is-remove"
          disabled={!onExpandableTableRemoveRow}
          title="Remove last row"
          onClick={() =>
            onExpandableTableRemoveRow?.(layout.field, Math.max(0, storedCount - 1))
          }
        >
          <DashCircle />
          <span>Remove</span>
        </button>
      )}
      <span className="dochub-sheet-renderer__table-actions-count">{visible}</span>
    </div>
  );
};

const SheetTable = ({
  sheet,
  templateSheets,
  appChoices,
  docSource,
  documentState,
  pickedInventory,
  onDocumentPathChange,
  onInventoryPathChange,
  onCustomTableCellChange,
  onExpandableTableAddRow,
  onExpandableTableRemoveRow,
  onSheetCheckboxChange
}) => {
  const expandedSheet = useMemo(
    () => expandSheetForCustomTables(sheet, documentState),
    [sheet, documentState]
  );

  const checkboxIndexMap = useMemo(() => buildCheckboxIndexMap(expandedSheet), [expandedSheet]);
  const checkboxDefaults = useMemo(
    () => listSheetCheckboxes(expandedSheet).map(cell => cell.defaultChecked),
    [expandedSheet]
  );

  const expandableActions = useMemo(() => {
    return resolveCustomSheetLayoutsWithRowShift(sheet.name, documentState)
      .filter(l => l.expandable)
      .map(layout => {
        const visible = getExpandableVisibleRowCount(documentState, layout);
        const storedCount = normalizeKeyValueTable(documentState?.[layout.field]).length;
        return {
          layout,
          visible,
          storedCount,
          lastRow1: layout.startRow + visible - 1,
          canAdd: visible < (layout.maxRows || 40),
          canRemove: storedCount > 0
        };
      });
  }, [sheet.name, documentState]);

  const layout = useMemo(() => {
    const base = buildRenderableCells(expandedSheet);
    return injectExpandableActionBars(base, expandableActions);
  }, [expandedSheet, expandableActions]);

  const formulaEngine = useSheetFormulaEngine({
    templateSheets: templateSheets?.length ? templateSheets : [sheet],
    activeSheetName: sheet.name,
    expandedSheet,
    documentState,
    pickedInventory,
    appChoices,
    docSource
  });

  return (
    <div className="dochub-sheet-renderer__scroll">
      <div
        className="dochub-sheet-renderer__grid"
        style={{
          width: layout.tableWidth,
          gridTemplateColumns: layout.gridTemplateColumns,
          gridTemplateRows: layout.gridTemplateRows,
          '--sheet-scale': layout.scale || SHEET_DISPLAY_SCALE
        }}
      >
        {(layout.actionBars || []).map(action => (
          <ExpandableTableActions
            key={action.layout.field}
            action={action}
            onExpandableTableAddRow={onExpandableTableAddRow}
            onExpandableTableRemoveRow={onExpandableTableRemoveRow}
          />
        ))}

        {layout.cells.map(cell => {
          const row1 = cell.row0 + 1;
          const col1 = cell.col0 + 1;
          const customBinding = getCustomCellBinding(
            sheet.name,
            row1,
            col1,
            documentState,
            appChoices
          );

          const variableMeta = cell.variableMeta;
          const checkboxMeta =
            !customBinding && cell.isCheckbox
              ? checkboxIndexMap[`${cell.row0},${cell.col0}`]
              : null;
          const mixedCell =
            !customBinding &&
            !checkboxMeta &&
            cell.isVariable &&
            isMixedVariableCell(variableMeta, cell.text);
          const primaryToken =
            !customBinding && !checkboxMeta
              ? getPrimaryVariableToken(variableMeta, cell.text)
              : null;
          const path = !mixedCell && primaryToken ? parseTokenPath(primaryToken) : null;
          const showSingleEditor =
            !customBinding &&
            !checkboxMeta &&
            cell.isVariable &&
            !mixedCell &&
            path?.length;

          let editor = null;

          if (customBinding) {
            const cellValue = getCustomTableCellValue(documentState, customBinding);
            const usesDatalist =
              customBinding.prop === 'key' || customBinding.prop === 'text';
            const listId = usesDatalist
              ? `dochub-table-${customBinding.field}-${row1}-${col1}`
              : undefined;
            const datalistOptions = listId
              ? getTableFieldDatalistOptions(appChoices, customBinding.field)
              : undefined;
            const showRowRemove =
              customBinding.expandable &&
              customBinding.prop === 'key' &&
              onExpandableTableRemoveRow;

            editor = (
              <div className={`dochub-variable-cell${showRowRemove ? ' has-row-action' : ''}`}>
                <SheetTextInput
                  type={customBinding.inputType}
                  value={cellValue === null || cellValue === undefined ? '' : String(cellValue)}
                  textAlign={cell.style?.textAlign || 'left'}
                  placeholder={customBinding.hint}
                  title={`${customBinding.field}[${customBinding.index}]${
                    customBinding.prop !== 'text' ? `.${customBinding.prop}` : ''
                  }`}
                  aria-label={customBinding.hint}
                  listId={listId}
                  datalistOptions={datalistOptions}
                  onChange={next => {
                    if (onCustomTableCellChange) {
                      onCustomTableCellChange(
                        customBinding.field,
                        customBinding.index,
                        customBinding.prop,
                        next
                      );
                    }
                  }}
                />
                {showRowRemove && (
                  <button
                    type="button"
                    className="dochub-sheet-renderer__row-remove"
                    title="Remove this row"
                    onClick={e => {
                      e.stopPropagation();
                      onExpandableTableRemoveRow(customBinding.field, customBinding.index);
                    }}
                  >
                    <DashCircle />
                  </button>
                )}
              </div>
            );
          } else if (checkboxMeta) {
            const checked = getSheetCheckboxValue(
              documentState,
              sheet.name,
              checkboxMeta.index,
              checkboxMeta.defaultChecked
            );
            editor = (
              <div className="dochub-variable-cell">
                <SheetCheckbox
                  checked={checked}
                  textAlign={cell.style?.textAlign || 'center'}
                  title={`Checkbox ${checkboxMeta.index + 1} (${sheet.name})`}
                  onChange={next => {
                    if (onSheetCheckboxChange) {
                      onSheetCheckboxChange(sheet.name, checkboxMeta.index, next, checkboxDefaults);
                    }
                  }}
                />
              </div>
            );
          } else if (mixedCell) {
            editor = (
              <MixedVariableCell
                variableMeta={variableMeta}
                cellText={cell.text}
                appChoices={appChoices}
                docSource={docSource}
                documentState={documentState}
                pickedInventory={pickedInventory}
                textAlign={cell.style?.textAlign || 'left'}
                onDocumentPathChange={onDocumentPathChange}
                onInventoryPathChange={onInventoryPathChange}
              />
            );
          } else if (showSingleEditor) {
            const binding = resolveVariableFieldBinding({
              path,
              appChoices,
              docSource,
              pickedInventory
            });
            const rawValue =
              binding.owner === 'inventory'
                ? getValueAtPath(pickedInventory, binding.statePath)
                : getValueAtPath(documentState, binding.statePath);
            const inventoryBound = binding.owner === 'inventory';

            editor = (
              <VariableCell
                binding={binding}
                value={rawValue}
                textAlign={cell.style?.textAlign || 'left'}
                title={primaryToken}
                disabled={inventoryBound && !pickedInventory}
                appChoices={appChoices}
                buyerEmail={documentState?.buyerEmail}
                onChange={next => {
                  if (inventoryBound) {
                    if (onInventoryPathChange) {
                      onInventoryPathChange(binding.statePath, next);
                    }
                  } else if (onDocumentPathChange) {
                    onDocumentPathChange(binding.statePath, next);
                  }
                }}
              />
            );
          }

          const showEditor = Boolean(editor);
          const isFormulaCell =
            !showEditor &&
            !customBinding &&
            (Boolean(cell.formula) || formulaEngine.hasFormula(cell.row0, cell.col0));
          const formulaDisplay = isFormulaCell
            ? formulaEngine.getFormulaDisplay(cell.row0, cell.col0)
            : null;
          const displayText = isFormulaCell
            ? formulaDisplay
            : isSheetErrorDisplay(cell.text)
              ? '—'
              : cell.text;

          return (
            <div
              key={cell.key}
              className={[
                'dochub-sheet-renderer__cell',
                `is-${cell.wrapMode || 'overflow'}`,
                cell.hasText || isFormulaCell || checkboxMeta ? 'has-text' : 'is-empty',
                cell.hasSheetBorder ? 'has-sheet-border' : '',
                cell.isVariable || customBinding || checkboxMeta ? 'is-variable' : '',
                mixedCell ? 'is-mixed-variable' : '',
                customBinding ? 'is-custom-table' : '',
                checkboxMeta ? 'is-checkbox' : '',
                isFormulaCell ? 'is-formula' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                ...cell.style,
                gridRow: cell.gridRow,
                gridColumn: cell.gridColumn,
                ...(cell.wrapMode === 'overflow' && cell.spillMaxWidth != null && !showEditor
                  ? { '--spill-max-width': `${cell.spillMaxWidth}px` }
                  : null)
              }}
              title={
                showEditor
                  ? customBinding
                    ? `${customBinding.field}[${customBinding.index}].${customBinding.prop}`
                    : variableMeta?.rawValue || primaryToken
                  : isFormulaCell
                    ? formulaEngine.getFormulaText(cell.row0, cell.col0) || cell.formula
                    : cell.formula || (cell.hasText ? cell.text : undefined)
              }
            >
              {showEditor ? (
                editor
              ) : (
                <span className="dochub-sheet-renderer__cell-text">{displayText}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SheetRenderer = ({
  documentLayout,
  documentName,
  appChoices,
  docSource,
  documentState,
  pickedInventory,
  onDocumentPathChange,
  onInventoryPathChange,
  onCustomTableCellChange,
  onExpandableTableAddRow,
  onExpandableTableRemoveRow,
  onSheetCheckboxChange
}) => {
  // Include hidden sheets in the formula workbook so cross-sheet refs still resolve.
  const allSheets = useMemo(() => documentLayout?.sheets || [], [documentLayout]);
  const sheets = useMemo(() => allSheets.filter(sheet => !sheet.hidden), [allSheets]);

  const [activeSheetName, setActiveSheetName] = useState(sheets[0]?.name);

  useEffect(() => {
    if (!sheets.length) {
      setActiveSheetName(undefined);
      return;
    }
    if (!sheets.some(sheet => sheet.name === activeSheetName)) {
      setActiveSheetName(sheets[0].name);
    }
  }, [sheets, activeSheetName]);

  if (!sheets.length) {
    return <div className="p-3 text-muted">No sheet layout available.</div>;
  }

  const activeSheet = sheets.find(sheet => sheet.name === activeSheetName) || sheets[0];
  return (
    <div className="dochub-sheet-renderer">
      <div className="dochub-sheet-renderer__toolbar">
        <div className="dochub-sheet-renderer__title">
          {documentName || documentLayout?.spreadsheetName || 'Document'}
        </div>
      </div>

      <div className="dochub-sheet-renderer__tabs">
        {sheets.map(sheet => (
          <button
            key={sheet.sheetId || sheet.name}
            type="button"
            className={`dochub-sheet-renderer__tab ${sheet.name === activeSheet.name ? 'is-active' : ''}`}
            onClick={() => setActiveSheetName(sheet.name)}
          >
            {sheet.name}
          </button>
        ))}
      </div>

      <SheetTable
        sheet={activeSheet}
        templateSheets={allSheets}
        appChoices={appChoices}
        docSource={docSource}
        documentState={documentState}
        pickedInventory={pickedInventory}
        onDocumentPathChange={onDocumentPathChange}
        onInventoryPathChange={onInventoryPathChange}
        onCustomTableCellChange={onCustomTableCellChange}
        onExpandableTableAddRow={onExpandableTableAddRow}
        onExpandableTableRemoveRow={onExpandableTableRemoveRow}
        onSheetCheckboxChange={onSheetCheckboxChange}
      />
    </div>
  );
};

export default SheetRenderer;
